import React, { useState, useEffect, useCallback } from 'react';
import Layout from 'Screens/Contextualization/Dashboard/components/Layout';
import ImportFileCreationModal from 'Screens/Contextualization/ManageEditCreate/components/ImportFileCreationModal';
import { getSummary, getStatsFiltered } from 'Screens/Contextualization/Dashboard/actions/Actions';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllHostsCountWs, selectCurrentWorkspace, selectIsReadOnly } from 'store/Faraday/selectors';

import EmptyVulns from 'Common/Components/EmptyVulns';
import emptyFeed from 'Images/empty-feed.png';
import ManualCreationModal from 'Screens/Contextualization/ManageEditCreate/components/ManualCreationModal';

import { StyledCol, Wrapper } from './Dashboard/styled';
import { getHostsCountInWs } from 'Screens/Contextualization/Faraday/actions/Actions';
import { selectIsFinishedLoad } from 'store/Dashboard/selectors';
import { closeModal, openModal } from 'store/modals/actions';
import { MODAL_CANT_CREATE } from 'store/modals/modals';
import selectModal from 'store/modals/selectors';
import WarningModal from 'Screens/Contextualization/ManageEditCreate/components/WarningModal';
import { CLOSED_FILTERS, CONFIRMED_FLAG_FILTERS } from 'store/Filters/constants';
import { isFilteringBy } from 'store/Filters/selectors';
import { buildVulnsCountParams } from 'services/api/dashboard/queryParams';
import get from 'lodash/get';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_BULK_CREATE, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import useReportFileDrop from 'Hooks/useReportFileDrop';
import ReportFileDropOverlay from 'Common/Components/ReportFileDropOverlay';

const DashboardLayout = () => {
  const dispatch = useDispatch();
  const workspaceSelected = useSelector(selectCurrentWorkspace);
  const stats = useSelector((state) => get(state, 'dashboard.tools[3].data.stats', {}));
  const totalVulns = get(stats, 'total_vulns', 0);
  const finishedLoad = useSelector(selectIsFinishedLoad);

  const assetsCount = useSelector(selectAllHostsCountWs);
  const noAssets = assetsCount === 0;
  const showModalCantCreate = useSelector((state) => selectModal(state, MODAL_CANT_CREATE));
  const readOnly = useSelector(selectIsReadOnly);
  const importVulnsAllowedByRole = useSelector((state) => selectAllowedByRole(state, FEATURE_BULK_CREATE, PERMISSION_TYPES.CREATE));

  const { confirmed } = CONFIRMED_FLAG_FILTERS;
  const { notClosed: closed } = CLOSED_FILTERS;

  const isFilteringByConfirm = useSelector((state) => isFilteringBy(state, 'vulns', confirmed));
  const isFilteringByClosed = useSelector((state) => isFilteringBy(state, 'vulns', closed));

  useEffect(() => {
    if (!isFilteringByConfirm && !isFilteringByClosed) {
      dispatch(getSummary());
    }
  }, [dispatch, workspaceSelected, isFilteringByConfirm, isFilteringByClosed]);

  useEffect(() => {
    if (isFilteringByConfirm || isFilteringByClosed) {
      const queryParams = buildVulnsCountParams({
        confirmed: isFilteringByConfirm,
        notClosed: isFilteringByClosed
      }).replace(/^&/, '');
      dispatch(getStatsFiltered(queryParams));
    }
  }, [dispatch, workspaceSelected, isFilteringByConfirm, isFilteringByClosed]);

  useEffect(() => {
    dispatch(getHostsCountInWs());
  }, [workspaceSelected, dispatch]);

  const [showModalUploadVulns, toggleModalUploadVulns] = useState(false);
  const [showModalCreateVuln, toggleModalCreateVuln] = useState(false);
  const [filesToImport, setFilesToImport] = useState([]);

  const openImportModal = useCallback(() => {
    setFilesToImport([]);
    toggleModalUploadVulns(true);
  }, []);

  const handleCloseImportModal = useCallback(() => {
    setFilesToImport([]);
    toggleModalUploadVulns(false);
  }, []);

  const handleDropReportFiles = useCallback((files) => {
    setFilesToImport(files);
    toggleModalUploadVulns(true);
  }, []);

  const { isDraggingFiles, dropHandlers } = useReportFileDrop({
    disabled: !workspaceSelected || readOnly || !importVulnsAllowedByRole || showModalUploadVulns,
    onDrop: handleDropReportFiles
  });

  const createManualVulns = () => {
    if (noAssets) dispatch(openModal(MODAL_CANT_CREATE));
    else toggleModalCreateVuln(true);
  };

  useEffect(() => () => {
    dispatch(closeModal(MODAL_CANT_CREATE));
  }, [dispatch]);

  if ((totalVulns === 0) && finishedLoad) {
    return (
      <Wrapper { ...dropHandlers }>
        <EmptyVulns image={ emptyFeed } toggleModalUploadVulns={ openImportModal } toggleModalCreateVuln={ createManualVulns } />
        { isDraggingFiles && <ReportFileDropOverlay /> }
        { showModalUploadVulns && <ImportFileCreationModal handleClose={ handleCloseImportModal } initialFiles={ filesToImport } /> }
        { showModalCreateVuln && <ManualCreationModal handleClose={ () => toggleModalCreateVuln(false) } entity="vulns" /> }
        { showModalCantCreate && <WarningModal entity="Vulnerability" /> }
      </Wrapper>
    );
  }

  return (
    <Wrapper { ...dropHandlers }>
      { isDraggingFiles && <ReportFileDropOverlay /> }
      <StyledCol>
        <Layout />
      </StyledCol>
      { showModalUploadVulns && <ImportFileCreationModal handleClose={ handleCloseImportModal } initialFiles={ filesToImport } /> }
    </Wrapper>
  );
};

export default DashboardLayout;
